import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Loader2, Copy, UserPlus, Trash2, ClipboardList, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HostGuestListPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [plus, setPlus] = useState(0);
  const [note, setNote] = useState('');
  const [bulk, setBulk] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/guest-lists/host/${token}`)).data); }
    catch { setNotFound(true); }
  }, [token]);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  const addOne = async () => {
    if (!first.trim()) return toast.error('First name required');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/guest-lists/host/${token}/entries`, {
        guests: [{ first_name: first, last_name: last, email, plus_ones: plus, note }],
      });
      if (r.data.added) { toast.success('Guest added'); setFirst(''); setLast(''); setEmail(''); setPlus(0); setNote(''); load(); }
      else toast.error('Not added — duplicate email, missing email, or the list is full');
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
    finally { setBusy(false); }
  };

  const parseLines = (text) => text.split(/\r?\n/).map(line => {
    const parts = line.includes(',') ? line.split(',') : line.trim().split(/\s+/);
    const cleaned = parts.map(p => p.trim()).filter(Boolean);
    if (!cleaned.length) return null;
    const emailIdx = cleaned.findIndex(p => p.includes('@'));
    const em = emailIdx >= 0 ? cleaned.splice(emailIdx, 1)[0] : '';
    const qty = cleaned.length && /^\d+$/.test(cleaned[cleaned.length - 1]) ? parseInt(cleaned.pop(), 10) : 1;
    return { first_name: cleaned[0] || '', last_name: cleaned.slice(1).join(' '), email: em, plus_ones: Math.max(0, qty - 1) };
  }).filter(Boolean);

  const addBulk = async (text) => {
    const guests = parseLines(text);
    if (!guests.length) return toast.error('Nothing to add');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/guest-lists/host/${token}/entries`, { guests });
      toast.success(`Added ${r.data.added} guest(s)${r.data.skipped_duplicates_or_invalid ? `, skipped ${r.data.skipped_duplicates_or_invalid}` : ''}`);
      setBulk(''); setShowBulk(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  const onCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      let text = String(ev.target.result || '');
      const lines = text.split(/\r?\n/);
      if (lines[0] && /first|name|email/i.test(lines[0])) lines.shift();
      addBulk(lines.join('\n'));
    };
    reader.readAsText(file);
  };

  const removeGuest = async (e) => {
    try { await axios.delete(`${API}/guest-lists/host/${token}/entries/${e.entry_id}`); toast.success('Removed'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Remove failed'); }
  };

  const copyClaim = () => { navigator.clipboard.writeText(data.claim_url); toast.success('Claim link copied — share it with your guests'); };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">This host link is not valid.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const { list, event, entries, counts } = data;
  const brand = list.branding || {};
  const statusColor = (s) => ({ claimed: 'text-emerald-400', checked_in: 'text-indigo-300', waitlist: 'text-amber-400', pending_approval: 'text-orange-300', declined: 'text-rose-400' }[s] || 'text-slate-400');

  return (
    <div className="min-h-screen bg-background pt-24 pb-10 px-4">
      <div className="max-w-3xl mx-auto space-y-4" data-testid="host-guest-list-page">
        <div className="text-center space-y-1">
          {brand.logo_url && <img src={brand.logo_url} alt="" className="h-10 mx-auto object-contain" />}
          <h1 className="text-2xl font-bold">{brand.company || list.host_name || 'Your'} — {list.name}</h1>
          <p className="text-sm text-muted-foreground">{event.name || event.title}{event.event_date ? ` · ${event.event_date}` : ''}{event.venue_name ? ` · ${event.venue_name}` : ''}</p>
          <Badge variant="outline" className={list.mode === 'ticketed' ? 'text-emerald-400 border-emerald-500/40' : 'text-amber-300 border-amber-500/40'}>
            {list.mode === 'ticketed' ? `Ticketed — guests get a QR ticket (${data.price_label})` : 'Door list — names at the door, no tickets'}
          </Badge>
        </div>

        {brand.welcome && <p className="text-sm text-center text-muted-foreground">{brand.welcome}</p>}
        {list.host_instructions && (
          <Card className="border-indigo-500/30 bg-indigo-500/5"><CardContent className="py-3 text-sm" data-testid="host-instructions">
            <strong className="text-indigo-300">From the organizer:</strong> {list.host_instructions}
          </CardContent></Card>
        )}

        <div className="grid grid-cols-3 gap-2 text-center">
          {[['Added', counts.added], ['Claimed', counts.claimed], ['Checked in', counts.checked_in]].map(([l, v]) => (
            <Card key={l}><CardContent className="py-3"><p className="text-2xl font-bold">{v}</p><p className="text-[10px] uppercase text-muted-foreground">{l}</p></CardContent></Card>
          ))}
        </div>
        {list.cap > 0 && <p className="text-xs text-center text-muted-foreground">{counts.people} of {list.cap} spots used{list.cutoff_at ? ` · adding closes ${new Date(list.cutoff_at).toLocaleString()}` : ''}</p>}

        <Button onClick={copyClaim} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="copy-claim-link">
          <Copy className="h-4 w-4 mr-1.5" /> Copy claim link to share with guests
        </Button>

        {data.cutoff_passed ? (
          <Card className="border-amber-500/40"><CardContent className="py-3 text-sm text-amber-300 text-center">The cutoff has passed — guests can no longer be added or edited.</CardContent></Card>
        ) : (
          <Card><CardContent className="pt-4 space-y-2">
            <p className="text-sm font-semibold flex items-center gap-1.5"><UserPlus className="h-4 w-4" /> Add a guest</p>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name" value={first} onChange={e => setFirst(e.target.value)} data-testid="host-add-first" />
              <Input placeholder="Last name" value={last} onChange={e => setLast(e.target.value)} data-testid="host-add-last" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input className="col-span-2" placeholder={list.mode === 'ticketed' ? 'Email (required)' : 'Email (optional)'} value={email} onChange={e => setEmail(e.target.value)} data-testid="host-add-email" />
              <Input type="number" min={0} max={list.plus_ones_max} placeholder="+1s" value={plus} onChange={e => setPlus(parseInt(e.target.value || 0, 10))} title={`Plus-ones (max ${list.plus_ones_max})`} />
            </div>
            <Input placeholder="Note for the door (e.g. VIP — take to owner's booth)" value={note} onChange={e => setNote(e.target.value)} data-testid="host-add-note" />
            <div className="flex gap-2">
              <Button onClick={addOne} disabled={busy} size="sm" data-testid="host-add-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add guest'}</Button>
              <Button variant="outline" size="sm" onClick={() => setShowBulk(s => !s)}><ClipboardList className="h-4 w-4 mr-1" /> Paste a list</Button>
              <label className="inline-flex items-center gap-1 text-xs border border-border rounded-md px-3 cursor-pointer hover:bg-muted/40">
                <Upload className="h-3.5 w-3.5" /> CSV
                <input type="file" accept=".csv,.txt" className="hidden" onChange={e => e.target.files[0] && onCsv(e.target.files[0])} />
              </label>
            </div>
            {showBulk && (
              <div className="space-y-2">
                <Textarea rows={5} value={bulk} onChange={e => setBulk(e.target.value)} placeholder={'One guest per line:\nJane Doe, jane@email.com, 2\nMarcus Lee marcus@email.com'} data-testid="host-bulk-text" />
                <Button size="sm" onClick={() => addBulk(bulk)} disabled={busy} data-testid="host-bulk-add">Add all</Button>
              </div>
            )}
          </CardContent></Card>
        )}

        <Card><CardContent className="pt-4">
          <p className="text-sm font-semibold mb-2">Your guests ({entries.length})</p>
          <div className="space-y-1">
            {entries.length === 0 && <p className="text-xs text-muted-foreground italic">No guests yet — add some above or share the claim link.</p>}
            {entries.map(e => (
              <div key={e.entry_id} className="flex items-center gap-2 text-xs border-b border-border/40 py-1.5" data-testid={`host-entry-${e.entry_id}`}>
                <span className="font-medium">{e.first_name} {e.last_name}</span>
                {e.plus_ones > 0 && <span className="text-muted-foreground">+{e.plus_ones}</span>}
                <span className="text-muted-foreground truncate max-w-[150px]">{e.email}</span>
                <span className={`ml-auto font-semibold uppercase text-[9px] ${statusColor(e.status)}`}>{(e.status || 'added').replace('_', ' ')}</span>
                {!data.cutoff_passed && !['claimed', 'checked_in'].includes(e.status) && (
                  <button onClick={() => removeGuest(e)} className="text-rose-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            ))}
          </div>
        </CardContent></Card>
        <p className="text-[10px] text-center text-muted-foreground">You'll get an email each time one of your guests checks in. Keep this page link private.</p>
      </div>
    </div>
  );
}
